import { useState, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useThemeColor } from "@/hooks/useThemeColor";

interface NotifyMeButtonProps {
  productId: number;
  productName: string;
}

type Step = "idle" | "form" | "submitting" | "success" | "already";

export default function NotifyMeButton({ productId, productName }: NotifyMeButtonProps) {
  const { primary, hexAlpha } = useThemeColor();
  const [step, setStep] = useState<Step>("idle");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const validatePhone = (val: string) =>
    /^(05\d{8}|009665\d{8}|\+9665\d{8})$/.test(val.replace(/\s/g, ""));

  const handleSubmit = async () => {
    const cleaned = phone.replace(/\s/g, "");
    if (!validatePhone(cleaned)) {
      setError("يرجى إدخال رقم جوال سعودي صحيح (05xxxxxxxx)");
      return;
    }
    setError("");
    setStep("submitting");

    try {
      // Check if already subscribed
      const { data: existing } = await supabase
        .from("stock_notifications")
        .select("id, status")
        .eq("product_id", productId)
        .eq("whatsapp", cleaned)
        .eq("status", "pending")
        .maybeSingle();

      if (existing) {
        setStep("already");
        return;
      }

      const { error: insertError } = await supabase
        .from("stock_notifications")
        .insert({
          product_id: productId,
          product_name: productName,
          whatsapp: cleaned,
          phone: cleaned,
          status: "pending",
        });

      if (insertError) throw insertError;
      setStep("success");
    } catch {
      setError("حدث خطأ، حاول مرة أخرى");
      setStep("form");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSubmit();
  };

  /* ── Success state ── */
  if (step === "success") {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-2xl px-6 py-5 text-center"
        style={{ background: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.25)", fontFamily: "'Tajawal', sans-serif" }}
        dir="rtl"
      >
        <div className="w-12 h-12 flex items-center justify-center bg-emerald-500/15 rounded-full">
          <i className="ri-notification-4-fill text-emerald-400 text-xl"></i>
        </div>
        <div>
          <p className="text-emerald-400 font-black text-sm">تم التسجيل بنجاح!</p>
          <p className="text-emerald-400/70 text-xs mt-1 leading-relaxed">
            سنرسل لك رسالة واتساب فور توفر المنتج
          </p>
        </div>
        <button
          onClick={() => { setStep("idle"); setPhone(""); }}
          className="text-emerald-400/60 text-xs underline cursor-pointer"
        >
          إلغاء الاشتراك
        </button>
      </div>
    );
  }

  /* ── Already subscribed ── */
  if (step === "already") {
    return (
      <div
        className="flex items-center gap-3 rounded-2xl px-5 py-4"
        style={{ background: hexAlpha(primary, 0.07), border: `1px solid ${hexAlpha(primary, 0.2)}`, fontFamily: "'Tajawal', sans-serif" }}
        dir="rtl"
      >
        <div className="w-9 h-9 flex items-center justify-center bg-amber-400/15 rounded-full flex-shrink-0">
          <i className="ri-time-line text-amber-400"></i>
        </div>
        <div className="flex-1">
          <p className="text-white font-bold text-sm">أنت مسجّل مسبقاً</p>
          <p className="text-gray-400 text-xs mt-0.5">سنُبلغك فور توفر هذا المنتج</p>
        </div>
        <button
          onClick={() => { setStep("idle"); setPhone(""); }}
          className="text-gray-600 text-xs cursor-pointer"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>
    );
  }

  /* ── Form expanded ── */
  if (step === "form" || step === "submitting") {
    return (
      <div
        className="flex flex-col gap-3 rounded-2xl px-5 py-4"
        style={{ background: "rgba(15,15,15,0.95)", border: `1px solid ${hexAlpha(primary, 0.2)}`, fontFamily: "'Tajawal', sans-serif" }}
        dir="rtl"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-red-500/15 rounded-full flex-shrink-0">
              <i className="ri-notification-4-line text-red-400 text-sm"></i>
            </div>
            <div>
              <p className="text-white font-black text-sm">أشعرني عند التوفر</p>
              <p className="text-gray-500 text-[11px]">سنرسل لك واتساب فور توفره</p>
            </div>
          </div>
          <button
            onClick={() => { setStep("idle"); setError(""); }}
            className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-gray-400 text-xs" style={{ fontFamily: "'Tajawal', sans-serif" }}>
            رقم واتساب
          </label>
          <div className="relative flex items-center">
            <span
              className="absolute right-3 text-xs text-gray-500 select-none pointer-events-none"
              style={{ fontFamily: "'Tajawal', sans-serif" }}
            >
              🇸🇦 +966
            </span>
            <input
              ref={inputRef}
              type="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setError(""); }}
              onKeyDown={handleKeyDown}
              placeholder="05XXXXXXXX"
              autoFocus
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pr-20 pl-4 py-3 text-white text-sm outline-none transition-colors placeholder-gray-600 focus:border-white/25"
              style={{ fontFamily: "'Tajawal', sans-serif" }}
            />
          </div>
          {error && (
            <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              <i className="ri-error-warning-line text-xs"></i>
              {error}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={step === "submitting" || !phone}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-black"
          style={{ background: `linear-gradient(to left, ${primary}, ${hexAlpha(primary, 0.8)})`, fontFamily: "'Tajawal', sans-serif" }}
        >
          {step === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              جاري التسجيل...
            </>
          ) : (
            <>
              <i className="ri-whatsapp-line text-sm"></i>
              تسجيل الإشعار
            </>
          )}
        </button>

        <p className="text-gray-600 text-[11px] text-center" style={{ fontFamily: "'Tajawal', sans-serif" }}>
          <i className="ri-lock-2-line ml-0.5"></i>
          رقمك لن يُشارك مع أي جهة
        </p>
      </div>
    );
  }

  /* ── Idle: main button ── */
  return (
    <button
      onClick={() => setStep("form")}
      className="group w-full flex items-center justify-center gap-2.5 py-4 rounded-full font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap relative overflow-hidden"
      style={{
        background: "rgba(239,68,68,0.08)",
        border: "1px solid rgba(239,68,68,0.25)",
        color: "#f87171",
        fontFamily: "'Tajawal', sans-serif",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.15)";
        (e.currentTarget as HTMLElement).style.borderColor = "rgba(239,68,68,0.4)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.08)";
        (e.currentTarget as HTMLElement).style.borderColor = "rgba(239,68,68,0.25)";
      }}
      dir="rtl"
    >
      <span className="relative z-10 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-400 animate-ping inline-block flex-shrink-0"></span>
        <i className="ri-notification-4-line text-base"></i>
        أشعرني عند التوفر
      </span>
    </button>
  );
}
